import { GifReader } from 'omggif'
import { identifyMedia, preloadImage, preloadVideo } from 'utils'
import { MediaInfo } from '../../types'
import { gifFrameDelaySeconds } from './gif/frame-delay'

const INFO_CACHE: { [key: string]: MediaInfo } = {}

async function getGifInfo(source: File | string): Promise<MediaInfo> {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source)
    const buffer = await fetch(url).then((res) => res.arrayBuffer())
    const view = new Uint8Array(buffer)
    const reader = new GifReader(view)
    const numFrames = reader.numFrames() || 0
    let duration = 0

    for (let i = 0; i < numFrames; i++) {
        const { delay } = reader.frameInfo(i)
        duration += gifFrameDelaySeconds(delay)
    }

    if (typeof source !== 'string') {
        URL.revokeObjectURL(url)
    }

    return {
        duration: Math.round(duration * 100) / 100,
        fps: Math.round(numFrames / duration),
        height: reader.height,
        type: 'gif',
        width: reader.width,
    }
}

async function getImageInfo(source: File | string): Promise<MediaInfo> {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source)
    const img = await preloadImage(url)

    if (typeof source !== 'string') {
        URL.revokeObjectURL(url)
    }

    return {
        duration: 0,
        height: img.naturalHeight,
        type: 'image',
        width: img.naturalWidth,
    }
}

async function getVideoInfo(source: File | string): Promise<MediaInfo> {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source)
    const video = await preloadVideo(url)

    if (typeof source !== 'string') {
        URL.revokeObjectURL(url)
    }

    return {
        duration: video.duration,
        height: video.videoHeight,
        type: 'video',
        width: video.videoWidth,
    }
}

export async function mediaInfoFromSource(source: File | string): Promise<MediaInfo | null> {
    const cacheId = typeof source === 'string' ? source : source.name

    if (INFO_CACHE[cacheId]) {
        return INFO_CACHE[cacheId]
    }

    const mediaType = identifyMedia(source)
    let info: MediaInfo | null

    switch (mediaType) {
        case 'gif':
            info = await getGifInfo(source)
            break
        case 'image':
            info = await getImageInfo(source)
            break
        case 'video':
            info = await getVideoInfo(source)
            break
        default:
            info = null
            break
    }

    if (info) {
        INFO_CACHE[cacheId] = info
    }

    return info
}
